/**
 * The Team dashboards' API surface — v19 D1 (the dev-head view) and D2 (who-does-what).
 *
 * ⚠ FILE NAMING: every file of the Team build in this folder is prefixed `team-` /
 * `Team…` because `features/dashboards/` is SHARED with the country / reports / group
 * screens (W2) — `api.ts` and `periods.ts` here are theirs. The period VOCABULARY is
 * imported from `./periods` rather than declared a second time: one web vocabulary,
 * one chip set, and `team-period-mirror.test.ts` holds that list against the TEAM
 * module's own API declaration (`team-periods.ts`), the third copy the sibling mirror
 * does not read.
 *
 * ===========================================================================
 * WHAT THESE TWO ROUTES ARE, AND WHAT THEY ARE NOT
 * ===========================================================================
 * `GET /team/workload` and `GET /throughput` are BARE paths — no `:cc` — because the
 * seat that asked for them (the Dev Head) leads an all-country team. Scope is the
 * TOKEN's: the service narrows to the countries where the seat holds the route's key
 * and ECHOES them back as `countries[]`. Nothing here sends a country filter, and
 * nothing here could — a seat cannot widen what it sees by editing a URL (proposal
 * §0.1). The per-country twin `GET /countries/:cc/throughput` exists on the API and is
 * deliberately NOT called from the web: the cross-country route already answers a
 * one-country seat with a one-country answer (`countries: ['IN']`), and one fetcher is
 * one fewer surface to keep honest.
 *
 * ===========================================================================
 * RULE 14 — MONEY ON D1, NO MONEY ON D2
 * ===========================================================================
 * D1 carries `amount` on exactly two node kinds — `in_flight[]` and `stuck_items[]` —
 * and every such node carries `country_code` on the SAME node, because the masking
 * interceptor resolves the nearest enclosing country and UNKNOWN MASKS. The figure
 * arrives as an exact DECIMAL string, or `'•••'` for a seat without `amounts.view` in
 * THAT country, or `null` when nobody priced the work. Three sentences, three renders
 * (`features/renewals/api.ts` records why they must never collapse). This module never
 * parses the string; `formatMoney` knows the marker.
 *
 * D2 carries NO money (proposal §8 #26) — counts, names, roles. The screen asserts it.
 *
 * ===========================================================================
 * NOT A SCOREBOARD
 * ===========================================================================
 * `developers[]` and `people[]` arrive in NAME order and are rendered in the order they
 * arrive. There is no rank column, no top-N, no sort-by-count control — the one list
 * ordered by a measure is `stuck_items[]`, and that orders WORK (longest in stage
 * first), never people. `team.dto.ts` on the API says the same in the same words, and
 * `team-workload.test.tsx` asserts the web keeps it.
 *
 * ⚠ EVERY TYPE BELOW MIRRORS `apps/api/src/dashboards/team/team.dto.ts` BY HAND. The
 * `@crf/shared` type-only rule (`lib/permissions.ts` header) is why: the DTOs live in
 * the API package, not in shared, so they are restated here.
 */

import type { CountryCode } from '../../domain/countries';
import { ApiRequestError, ApiTransportError, apiRequest } from '../../lib/api';
import type { DashboardPeriod } from './periods';

/* ──────────────────────────────────────────────────────── the vocabularies ── */

/**
 * The five buckets, in the order the brief lists them plus the two the vocabulary forces.
 * Mirror of `WORKLOAD_BUCKETS` in `team.dto.ts`; `blocked` is `work_orders.status =
 * 'testing_failed'` — returned work, the board's "Correction" column — and keeps the
 * brief's word as its KEY while the screen labels it "Returned" (proposal §2.1).
 */
export const WORKLOAD_BUCKETS = ['assigned', 'in_progress', 'in_testing', 'blocked', 'ready'] as const;
export type WorkloadBucket = (typeof WORKLOAD_BUCKETS)[number];

/** What each bucket is called on screen. `blocked` → "Returned": see `WORKLOAD_BUCKETS`. */
export const BUCKET_LABELS: Readonly<Record<WorkloadBucket, string>> = {
  assigned: 'Assigned',
  in_progress: 'In progress',
  in_testing: 'In testing',
  blocked: 'Returned',
  ready: 'Ready',
};

/** The period key as the API spells it — `DashboardPeriod` is the one web vocabulary. */
export type ReportPeriod = DashboardPeriod;

/* ───────────────────────────────────────────────────────────── D1 — shapes ── */

export interface WorkloadBucketsDto {
  assigned: number;
  in_progress: number;
  in_testing: number;
  blocked: number;
  ready: number;
  /** The five above summed — this developer's open work. Not money. */
  total: number;
}

export interface CountryCountDto {
  country_code: CountryCode;
  count: number;
}

/** MONEY NODE — `country_code` on the node is what the interceptor keys the mask on. */
export interface InFlightMoneyDto {
  country_code: CountryCode;
  currency: string | null;
  /** Exact DECIMAL string · `'•••'` (masked for this seat in this country) · `null` (nobody priced it). */
  amount: string | null;
  /** Open work orders the sum covers (rows with a NULL amount still count here). */
  count: number;
}

export interface DeveloperWorkloadDto {
  user_id: string;
  name: string;
  buckets: WorkloadBucketsDto;
  /** Open work orders at or past `stuck_after_days` in their stage. */
  stuck: number;
  by_country: CountryCountDto[];
  in_flight: InFlightMoneyDto[];
}

export type StageSinceSource = 'event' | 'created_at';
export type StuckBucket = WorkloadBucket | 'unassigned';

/** MONEY NODE — carries `country_code` for the same reason as {@link InFlightMoneyDto}. */
export interface StuckItemDto {
  work_order_id: string;
  crf_id: string;
  crf_doc_no: string | null;
  country_code: CountryCode;
  party_name: string | null;
  status: string;
  bucket: StuckBucket;
  assigned_to: { id: string; name: string } | null;
  /** ISO instant the current stage began (server-derived from the event spine; see `team.dto.ts`). */
  stage_since: string;
  /** Which clock was used — the event spine, or `created_at` for a pre-spine row. */
  stage_since_source: StageSinceSource;
  /** Whole days in the current stage — a FLOOR on staleness, never an overstatement. */
  days_in_stage: number;
  amount: string | null;
  currency: string | null;
}

export interface TeamWorkloadDto {
  /** The countries the TOKEN admitted (crf.view), echoed — never a query parameter. */
  countries: CountryCode[];
  generated_at: string;
  stuck_after_days: number;
  /** NAME order, then user_id. A user with no open work has no row (no roster exists). */
  developers: DeveloperWorkloadDto[];
  unassigned: { total: number; stuck: number; by_country: CountryCountDto[] };
  /** Longest in stage first — the chase list orders WORK, not people. */
  stuck_items: StuckItemDto[];
}

/* ───────────────────────────────────────────────────────────── D2 — shapes ── */

export interface TeamPeriodWindowDto {
  key: ReportPeriod;
  /** Civil dates, inclusive; on the cross-country route the echoed `custom` ends or null. */
  from: string | null;
  to: string | null;
}

export interface CountryWindowDto {
  country_code: CountryCode;
  tz: string;
  from: string;
  to: string;
  from_at: string;
  to_at: string;
}

export interface PersonThroughputDto {
  user_id: string;
  name: string;
  country_code: CountryCode;
  roles: string[];
  crfs_delivered: number;
  test_rounds: number;
  deployments: number;
  signoffs_developer: number;
  signoffs_project: number;
}

export interface TeamThroughputDto {
  country_code: CountryCode;
  role_name: string | null;
  people: number;
  crfs_delivered: number;
  test_rounds: number;
  deployments: number;
  signoffs: number;
}

export interface ThroughputDto {
  window: TeamPeriodWindowDto;
  /** One per admitted country — each resolves the period in ITS calendar. */
  windows: CountryWindowDto[];
  countries: CountryCode[];
  /** NAME order, then country, then user_id. Only people with a non-zero counter appear. */
  people: PersonThroughputDto[];
  /** (country, role template) — the only organisational axis that exists. */
  teams: TeamThroughputDto[];
  /** Deliveries through a work order with no assignee — reported so the columns reconcile. */
  unattributed: { crfs_delivered: number };
}

export interface ThroughputFilters {
  /**
   * ⚠ PASSED THROUGH UNVALIDATED — the renewals register's reason: the server owns the
   * vocabulary and answers a stranger with a 400 that NAMES the allowed values. Sanitising
   * here would render a default window while the chip claimed another.
   */
  period?: string;
  from?: string;
  to?: string;
}

/* ─────────────────────────────────────────────────────────────── fetchers ── */

export async function fetchTeamWorkload(signal?: AbortSignal): Promise<TeamWorkloadDto> {
  return apiRequest<TeamWorkloadDto>('/team/workload', { signal });
}

export async function fetchThroughput(
  filters: ThroughputFilters = {},
  signal?: AbortSignal,
): Promise<ThroughputDto> {
  return apiRequest<ThroughputDto>('/throughput', {
    query: { period: filters.period, from: filters.from, to: filters.to },
    signal,
  });
}

/* ───────────────────────────────────────────────────────────────── errors ── */

/** The sentence the screen shows. On a 400 the server's own message names the vocabulary. */
export function teamErrorText(error: unknown): string {
  if (error instanceof ApiTransportError) {
    return 'The server could not be reached. Nothing was changed.';
  }
  if (error instanceof ApiRequestError) {
    if (error.status === 403) {
      return 'This account is not allowed to see this view.';
    }
    return error.message;
  }
  return 'Something went wrong. Nothing was changed.';
}

/* ─────────────────────────────────────────────────────────────── query keys ── */

export const teamKeys = {
  workload: () => ['dashboards', 'team', 'workload'] as const,
  throughput: (filters: ThroughputFilters) => ['dashboards', 'team', 'throughput', filters] as const,
};
